import { FormEvent, useState } from 'react'
import { useLang, t } from '../lib/i18n'
import { Check, IdCard, User, X } from 'lucide-react'
import { patientsApi, type RegistrationResponse } from '../lib/api'
import {
  appendOfflineRegistration,
  generateLocalNhi,
  isApiLikelyDown,
  type OfflineRegistration,
} from '../lib/offlineStore'

/**
 * Patient registration form (Sprint 2 — Minimum Registry).
 *
 * Collects a structured Syrian name (given / father / grandfather / family) in
 * both Arabic and Latin, an identifier (National ID / passport), DOB, sex and
 * phone, then POSTs to /api/v1/patients/register.
 *
 * The structured name components are joined into the flattened `name_ar` /
 * `name_latin` fields the API expects (ADR-004 three-representation rule).
 *
 * Offline fallback (static Cloudflare Pages demo): when the API is
 * unreachable (ApiError — e.g. pages.dev with no backend), registration is
 * completed locally: an NHI is generated in the backend's exact contract
 * format (SYR-<year>-<5 digits><Luhn check>), the RegistrationResponse is
 * assembled contract-exactly, persisted to localStorage, and merged into the
 * offline roster so PatientSearch / ProviderExperience can open the record.
 * Offline-created records are badged as sample data — see `registeredOffline`.
 */

type NameParts = { given: string; father: string; grandfather: string; family: string }

const EMPTY_NAME: NameParts = { given: '', father: '', grandfather: '', family: '' }

/**
 * Offline registration helpers — ids come from crypto.randomUUID(), the NHI
 * sequence from Date.now() (see offlineStore.ts).
 */
function newOfflineIds(): { personId: string; patientId: string } {
  return { personId: crypto.randomUUID(), patientId: crypto.randomUUID() }
}

function buildOfflineRegistration(
  payload: Parameters<typeof patientsApi.register>[0],
): { response: RegistrationResponse; entry: OfflineRegistration } {
  const { personId, patientId } = newOfflineIds()
  // Date.now()-derived sequence, clamped to 5 digits — sequential-ish so
  // demo NHIs issued moments apart never collide (check digit appended by
  // generateLocalNhi keeps the backend's SYR-<year>-XXXXXX format).
  const nhi = generateLocalNhi(Date.now() % 100000)
  const mrn = `MRN-${nhi.slice(-6)}`
  const patient = {
    id: patientId,
    person_id: personId,
    facility_id: payload.facility?.facility_id ?? 'fac-001',
    mrn,
    verification_tier: payload.facility?.verification_tier ?? 1,
    status: 'active',
    person: {
      id: personId,
      nhi,
      name_ar: payload.name_ar ?? null,
      name_latin: payload.name_latin ?? null,
      dob: payload.dob ?? null,
      sex: payload.sex ?? null,
      phone: payload.phone ?? null,
    },
  }
  return {
    response: { patient, mpi_match: null, action: 'created_new' },
    entry: { patient, action: 'created_new', created_at: new Date().toISOString() },
  }
}

function joinName(parts: NameParts): string {
  return [parts.given, parts.father, parts.grandfather, parts.family]
    .map((part) => part.trim())
    .filter(Boolean)
    .join(' ')
}

/**
 * Standalone component for the four-field name group (given / father /
 * grandfather / family).
 *
 * Extracted from the inline `nameField` render-prop to fix a focus-loss bug:
 * defining the JSX inline created a new component identity on every parent
 * render, causing React to remount these inputs and drop focus on each
 * keystroke.
 */
function NameFieldGroup({
  value,
  onChange,
}: {
  value: NameParts
  onChange: (parts: NameParts) => void
}) {
  return (
    <div className="grid gap-3 md:grid-cols-2">
      {(
        [
          ['given', t('reg.given')],
          ['father', t('reg.father')],
          ['grandfather', t('reg.grandfather')],
          ['family', t('reg.family')],
        ] as const
      ).map(([key, fieldLabel]) => (
        <label key={key} className="grid gap-1.5 text-sm font-medium text-stone-700">
          {fieldLabel}
          <input
            dir="auto"
            value={value[key]}
            onChange={(event) => onChange({ ...value, [key]: event.target.value })}
            className="h-11 rounded-lg border border-stone-200 bg-white px-3 text-stone-950 outline-none transition placeholder:text-stone-400 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
          />
        </label>
      ))}
    </div>
  )
}

export function PatientRegistration({
  onRegistered,
  onCancel,
}: {
  onRegistered: (result: RegistrationResponse) => void
  onCancel: () => void
}) {
  const [nameAr, setNameAr] = useState<NameParts>(EMPTY_NAME)
  const [nameLatin, setNameLatin] = useState<NameParts>(EMPTY_NAME)
  const [identifierType, setIdentifierType] = useState<'national' | 'passport'>('national')
  const [identifier, setIdentifier] = useState('')
  const [dob, setDob] = useState('')
  const [sex, setSex] = useState<'M' | 'F' | ''>('')
  const [phone, setPhone] = useState('')
  const [governorate, setGovernorate] = useState('')
  const [city, setCity] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [offlineMode, setOfflineMode] = useState(false)

  const nameArFull = joinName(nameAr)
  const nameLatinFull = joinName(nameLatin)

  const isNationalIdVerified =
    identifierType === 'national' && /^\d{11}$/.test(identifier)

  async function submit(event: FormEvent) {
    event.preventDefault()
    setError('')

    if (!nameArFull && !nameLatinFull) {
      setError(t('reg.nameRequired'))
      return
    }
    if (identifierType === 'national' && identifier && !/^\d{11}$/.test(identifier)) {
      setError(t('reg.nidDigits'))
      return
    }

    setSubmitting(true)
    setOfflineMode(false)
    try {
      const result = await patientsApi.register({
        name_ar: nameArFull || undefined,
        name_latin: nameLatinFull || undefined,
        // National ID maps to the MOI ID field; passport is left as a free
        // identifier (the API currently keys on moi_id).
        moi_id: identifierType === 'national' && identifier ? identifier : undefined,
        dob: dob || undefined,
        sex: sex || undefined,
        phone: phone || undefined,
        address:
          governorate || city
            ? {
                ...(governorate ? { governorate } : {}),
                ...(city ? { city } : {}),
              }
            : undefined,
        facility: {
          facility_id: 'fac-001',
          mrn: `MRN-${Date.now().toString().slice(-6)}`,
          verification_tier: 1,
          registration_source: 'web-registration',
        },
      })
      onRegistered(result)
    } catch (err) {
      // Static-site / backend-unreachable fallback: complete the registration
      // locally (contract-exact response + localStorage persistence) instead
      // of surfacing a dead-end error. Shared detection logic lives in
      // offlineStore.isApiLikelyDown; genuine live-API rejections (422
      // validation, 409 conflict) still show the server's message honestly.
      if (!isApiLikelyDown(err)) {
        setError(err instanceof Error ? err.message : t('reg.failed'))
        return
      }
      const offline = buildOfflineRegistration({
        name_ar: nameArFull || undefined,
        name_latin: nameLatinFull || undefined,
        moi_id: identifierType === 'national' && identifier ? identifier : undefined,
        dob: dob || undefined,
        sex: sex || undefined,
        phone: phone || undefined,
        address:
          governorate || city
            ? { ...(governorate ? { governorate } : {}), ...(city ? { city } : {}) }
            : undefined,
        facility: {
          facility_id: 'fac-001',
          mrn: `MRN-${Date.now().toString().slice(-6)}`,
          verification_tier: 1,
          registration_source: 'web-registration',
        },
      })
      appendOfflineRegistration(offline.entry)
      setOfflineMode(true)
      onRegistered(offline.response)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <section
      role="dialog"
      aria-modal="true"
      aria-labelledby="register-title"
      className="animate-pop-in rounded-xl border border-emerald-100 bg-white p-5 shadow-lift"
    >
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-lg bg-primary-50 text-primary-700">
            <User className="h-5 w-5" />
          </div>
          <div>
            <h3 id="register-title" className="text-lg font-semibold text-stone-950">
              {t('reg.title')}
            </h3>
            <p className="text-xs text-stone-500">{t('reg.sub')}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={onCancel}
          aria-label={t('reg.closeAria')}
          className="grid h-9 w-9 place-items-center rounded-lg border border-stone-200 text-stone-600 transition hover:bg-stone-50 active:scale-90"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <form onSubmit={submit} className="grid gap-5">
        {/* Arabic name */}
        <fieldset className="grid gap-2 rounded-lg border border-stone-100 bg-stone-50/50 p-4">
          <legend className="px-1 text-sm font-semibold text-stone-800">
            {t('reg.arabicBlock')}
          </legend>
          <NameFieldGroup value={nameAr} onChange={setNameAr} />
        </fieldset>

        {/* Latin name */}
        <fieldset className="grid gap-2 rounded-lg border border-stone-100 bg-stone-50/50 p-4">
          <legend className="px-1 text-sm font-semibold text-stone-800">
            {t('reg.latinBlock')}
          </legend>
          <NameFieldGroup value={nameLatin} onChange={setNameLatin} />
        </fieldset>

        {/* Identifier */}
        <fieldset className="grid gap-3 rounded-lg border border-stone-100 bg-stone-50/50 p-4">
          <legend className="px-1 text-sm font-semibold text-stone-800">{t('reg.identifier')}</legend>
          <div className="flex gap-2">
            {(
              [
                ['national', t('reg.nationalId')],
                ['passport', t('reg.passport')],
              ] as const satisfies ReadonlyArray<readonly [string, string]> satisfies ReadonlyArray<readonly [string, string]>
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setIdentifierType(value)}
                aria-pressed={identifierType === value}
                className={`flex h-10 items-center gap-2 rounded-lg border px-3 text-sm font-semibold transition active:scale-95 ${
                  identifierType === value
                    ? 'border-primary-200 bg-primary-50 text-primary-800'
                    : 'border-stone-200 bg-white text-stone-600 hover:border-emerald-200'
                }`}
              >
                <IdCard className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>
          <label className="grid gap-1.5 text-sm font-medium text-stone-700">
            {identifierType === 'national' ? t('reg.nationalIdField') : t('reg.passportField')}
            <div className="relative">
              <input
                dir="auto"
                value={identifier}
                onChange={(event) => setIdentifier(event.target.value)}
                placeholder={identifierType === 'national' ? '12345678901' : t('ph.passportSample')}
                className="h-11 w-full rounded-lg border border-stone-200 bg-white px-3 text-stone-950 outline-none transition placeholder:text-stone-400 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
              />
              {isNationalIdVerified && (
                <span className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800">
                  <Check className="h-3.5 w-3.5" />
                  Verified
                </span>
              )}
            </div>
          </label>
        </fieldset>

        {/* Demographics */}
        <div className="grid gap-3 md:grid-cols-3">
          <label className="grid gap-1.5 text-sm font-medium text-stone-700">
            {t('reg.dob')}
            <input
              type="date"
              value={dob}
              onChange={(event) => setDob(event.target.value)}
              className="h-11 rounded-lg border border-stone-200 bg-white px-3 text-stone-950 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
            />
          </label>
          <label className="grid gap-1.5 text-sm font-medium text-stone-700">
            {t('reg.sex')}
            <select
              value={sex}
              onChange={(event) => setSex(event.target.value as 'M' | 'F' | '')}
              className="h-11 rounded-lg border border-stone-200 bg-white px-3 text-stone-950 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
            >
              <option value="">{t('domain.selectPlaceholder')}</option>
              <option value="M">{t('reg.male')}</option>
              <option value="F">{t('reg.female')}</option>
            </select>
          </label>
          <label className="grid gap-1.5 text-sm font-medium text-stone-700">
            {t('reg.phone')}
            <input
              dir="auto"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              placeholder="+963 944 123 456"
              className="h-11 rounded-lg border border-stone-200 bg-white px-3 text-stone-950 outline-none transition placeholder:text-stone-400 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
            />
          </label>
        </div>

        {/* Address */}
        <div className="grid gap-3 md:grid-cols-2">
          <label className="grid gap-1.5 text-sm font-medium text-stone-700">
            {t('reg.governorate')}
            <input
              dir="auto"
              value={governorate}
              onChange={(event) => setGovernorate(event.target.value)}
              placeholder={t('ph.governorate')}
              className="h-11 rounded-lg border border-stone-200 bg-white px-3 text-stone-950 outline-none transition placeholder:text-stone-400 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
            />
          </label>
          <label className="grid gap-1.5 text-sm font-medium text-stone-700">
            {t('reg.city')}
            <input
              dir="auto"
              value={city}
              onChange={(event) => setCity(event.target.value)}
              placeholder={t('ph.city')}
              className="h-11 rounded-lg border border-stone-200 bg-white px-3 text-stone-950 outline-none transition placeholder:text-stone-400 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
            />
          </label>
        </div>

        {offlineMode && (
          <p
            role="status"
            data-testid="offline-registration-badge"
            className="flex items-center gap-2 text-sm text-amber-800"
          >
            <span
              className="inline-flex items-center rounded-md border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800"
              data-testid="sample-data-badge"
            >
              {t('prov.sampleData')}
            </span>
            {t('prov.offlineNote')}
          </p>
        )}

        {error && (
          <p
            className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-medium text-rose-800"
            role="alert"
          >
            {error}
          </p>
        )}

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={submitting}
            className="flex h-11 items-center justify-center gap-2 rounded-lg bg-primary-700 px-4 text-sm font-semibold text-white shadow-md shadow-emerald-900/15 transition hover:bg-primary-800 active:scale-95 disabled:opacity-60"
          >
            <Check className="h-4 w-4" />
            {submitting ? t('reg.registering') : t('reg.submit')}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="flex h-11 items-center justify-center gap-2 rounded-lg border border-stone-200 bg-white px-4 text-sm font-semibold text-stone-700 transition hover:bg-stone-50 active:scale-95"
          >
            {t('reg.cancel')}
          </button>
        </div>
      </form>
    </section>
  )
}